import { useEffect, useState } from "react";
import { MeDto } from "../Api/admin.api";
import { TENANT_CHANGED_EVENT } from "../services/TenantService";
import { cachedMe, clearMeCache, loadMe } from "../services/MeCache";
import { useAuth } from "../context/AuthContext";

/**
 * Identity for display: who is signed in, their role, and the tenant in view.
 *
 * Deliberately a plain REST call to GET /api/admin/me rather than the `user`
 * socket event. The socket needs a connection and a `getUser` round trip before
 * it has anything, so the app bar rendered a "User" placeholder until that
 * resolved -- and permanently whenever the socket did not connect. This resolves
 * from the database on one request and is also where the role and account NAMES
 * come from, which the stored login payload does not carry (it has ids only).
 *
 * Shared by the app bar and the tenant switcher, and the response is cached in
 * MeCache, so a page load makes one call.
 */

export type UseMeResult = {
    me: MeDto | null;
    loading: boolean;
    error: string | null;
};

export function useMe(): UseMeResult {
    const { isAuthenticated } = useAuth();
    const [me, setMe] = useState<MeDto | null>(cachedMe());
    const [loading, setLoading] = useState(!cachedMe());
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isAuthenticated) {
            clearMeCache();
            setMe(null);
            setLoading(false);
            return;
        }

        let cancelled = false;

        const fetch = (force: boolean) => {
            setLoading(!cachedMe());
            loadMe(force)
                .then((next) => {
                    if (cancelled) return;
                    setMe(next);
                    setError(null);
                    setLoading(false);
                })
                .catch((err) => {
                    if (cancelled) return;
                    setError(err?.message || "Failed to load user");
                    setLoading(false);
                });
        };

        fetch(false);

        // A tenant switch changes actingAccountName, so refetch. (The switcher
        // also reloads the page, but this keeps the hook correct on its own.)
        const onTenantChange = () => fetch(true);
        window.addEventListener(TENANT_CHANGED_EVENT, onTenantChange);

        return () => {
            cancelled = true;
            window.removeEventListener(TENANT_CHANGED_EVENT, onTenantChange);
        };
    }, [isAuthenticated]);

    return { me, loading, error };
}

export default useMe;
